import { Printer } from 'lucide-react'
import React, { useState, useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import styles from '~/styles/NodeDocument.module.css'
import MarkdownRenderer from '../markdown-renderer/MarkdownRenderer'

// Utility function to process template variables
const processTemplate = (template: string, variables: Record<string, string | number>) => {
  let processed = template;
  Object.entries(variables).forEach(([key, value]) => {
    const regex = new RegExp(`\\[%${key}%\\]`, 'g');
    processed = processed.replace(regex, String(value));
  });
  processed = processed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  return processed;
};

// Utility function to clean markdown for printing
const cleanMarkdownForPrinting = (markdown: string) => {
  return markdown
    .replace(/:::\s*section\s+.*$/gm, '')
    .replace(/^:::$/gm, '')
    .replace(/\n\n\n+/gm, '\n\n')
    .trim()
}

interface PrintingTabProps {
  contentMarkdown: string
  coverMarkdown?: string
  isMarkdownExpanded: boolean
  markdownHeight: string
  nodebaseHeight: string
  printSettings?: {
    headerText: string
    footerText: string
    coverMarkdown: string
  }
  onPrintSettingsChange?: (settings: { headerText: string; footerText: string; coverMarkdown: string }) => void
  data?: {
    label?: string
    assignedEmails?: string[]
    createdBy?: string
  }
}

const PrintingTab: React.FC<PrintingTabProps> = ({
  contentMarkdown,
  coverMarkdown = '',
  isMarkdownExpanded,
  markdownHeight,
  nodebaseHeight,
  printSettings = { headerText: '', footerText: '', coverMarkdown: '' },
  onPrintSettingsChange,
  data,
}) => {
  const [localHeaderText, setLocalHeaderText] = useState(printSettings?.headerText || '')
  const [localFooterText, setLocalFooterText] = useState(printSettings?.footerText || '')
  const [localCoverMarkdown, setLocalCoverMarkdown] = useState(printSettings?.coverMarkdown || '')
  const printableRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setLocalHeaderText(printSettings?.headerText || '')
    setLocalFooterText(printSettings?.footerText || '')
    setLocalCoverMarkdown(printSettings?.coverMarkdown || '')
  }, [printSettings])

  const handleHeaderTextBlur = () => {
    if (onPrintSettingsChange) {
      onPrintSettingsChange({
        ...printSettings,
        headerText: localHeaderText
      })
    }
  }

  const handleFooterTextBlur = () => {
    if (onPrintSettingsChange) {
      onPrintSettingsChange({
        ...printSettings,
        footerText: localFooterText
      })
    }
  }

  const handleCoverMarkdownBlur = () => {
    if (onPrintSettingsChange) {
      onPrintSettingsChange({
        ...printSettings,
        coverMarkdown: localCoverMarkdown
      })
    }
  }

  const handlePrint = async () => {
    const headerText = printSettings.headerText || '';
    const footerText = printSettings.footerText || '';
    const coverMarkdown = printSettings.coverMarkdown || '';
    const now = new Date();
    const date = now.toISOString().slice(0, 10);
    const time = now.toTimeString().slice(0, 5);
    const watermarkText = `DRAFT\nSpechops\n${date} ${time}`;
    const processedHeader = processTemplate(headerText, {
      DATE: new Date().toLocaleDateString(),
      TIME: new Date().toLocaleTimeString(),
      YEAR: new Date().getFullYear(),
      MONTH: new Date().toLocaleString('default', { month: 'long' }),
      DAY: new Date().getDate(),
      DOCUMENT_TITLE: data?.label || 'Untitled Document',
      AUTHOR: data?.createdBy || 'Unknown',
      VERSION: '1.0',
      COMPANY: 'Spechops',
    });
    const processedFooter = processTemplate(footerText, {
      DATE: new Date().toLocaleDateString(),
      TIME: new Date().toLocaleTimeString(),
      YEAR: new Date().getFullYear(),
      MONTH: new Date().toLocaleString('default', { month: 'long' }),
      DAY: new Date().getDate(),
      DOCUMENT_TITLE: data?.label || 'Untitled Document',
      AUTHOR: data?.createdBy || 'Unknown',
      VERSION: '1.0',
      COMPANY: 'Spechops',
    });
    const processedCoverMarkdown = processTemplate(coverMarkdown, {
      DATE: new Date().toLocaleDateString(),
      TIME: new Date().toLocaleTimeString(),
      YEAR: new Date().getFullYear(),
      MONTH: new Date().toLocaleString('default', { month: 'long' }),
      DAY: new Date().getDate(),
      DOCUMENT_TITLE: data?.label || 'Untitled Document',
      AUTHOR: data?.createdBy || 'Unknown',
      VERSION: '1.0',
      COMPANY: 'Spechops',
    });
    const documentTitle = data?.label || 'Untitled Document';
    const author = data?.createdBy || 'Unknown';
    // Render markdown to HTML (use the same renderer as the preview)
    const printableContent = document.getElementById('printable-content')?.innerHTML;
    if (!printableContent) return;
    
    // Create cover page HTML if cover markdown exists
    let coverPageHTML = '';
    if (processedCoverMarkdown.trim()) {
      // Create a temporary div to render the cover markdown
      const tempCoverDiv = document.createElement('div');
      tempCoverDiv.innerHTML = '<div id="cover-content"></div>';
      document.body.appendChild(tempCoverDiv);
      
      // Use the same MarkdownRenderer logic (simplified for this context)
      // For now, we'll create a simple cover page structure
      coverPageHTML = `
        <div class="cover-page" style="page-break-after: always; text-align: center; padding: 4cm 2cm; min-height: 29.7cm; display: flex; flex-direction: column; justify-content: center; align-items: center;">
          <div style="font-size: 2.5rem; font-weight: bold; margin-bottom: 2rem; line-height: 1.2;">
            ${processedCoverMarkdown.replace(/#\s*(.*)/g, '<h1>$1</h1>').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}
          </div>
        </div>
      `;
      
      document.body.removeChild(tempCoverDiv);
    }
    
    // Create a temporary container to measure and split content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = printableContent;
    tempDiv.style.position = 'absolute';
    tempDiv.style.visibility = 'hidden';
    tempDiv.style.width = '18cm'; // A4 width minus margins
    tempDiv.style.padding = '0';
    tempDiv.style.boxSizing = 'border-box';
    tempDiv.style.fontFamily = 'system-ui, -apple-system, BlinkMacSystemFont, "Inter", Roboto, sans-serif';
    tempDiv.style.fontSize = '14px';
    tempDiv.style.lineHeight = '1.6';
    document.body.appendChild(tempDiv);
    
    // Calculate page size in px
    const pxPerCm = 37.8;
    const pageHeight = 29.7 * pxPerCm;
    const marginTop = 0 * pxPerCm;
    const marginBottom = 0 * pxPerCm;
    const headerHeight = 1.5 * pxPerCm;
    const footerHeight = 1.5 * pxPerCm;
    const availableHeight = pageHeight - marginTop - marginBottom - headerHeight - footerHeight;
    
    console.log('Page calculation:', {
      pageHeight,
      marginTop,
      marginBottom,
      headerHeight,
      footerHeight,
      availableHeight,
      contentHeight: tempDiv.scrollHeight
    });
    
    // Calculate total pages needed (simplified for CSS Paged Media)
    const totalContentHeight = tempDiv.scrollHeight;
    const totalPages = Math.ceil(totalContentHeight / availableHeight);
    
    console.log('Content splitting:', {
      totalContentHeight,
      totalPages,
      availableHeight
    });
    
    document.body.removeChild(tempDiv);
    
    // Build HTML for print window - CSS Paged Media approach
    const htmlContent = `<!DOCTYPE html>
<html>
<head>
<title>Print Document</title>
<meta charset="utf-8">
<style>
@page { 
  size: A4; 
  margin: 1cm; 
  @top-center {
    content: "${processedHeader.replace(/"/g, '\\"')}";
    font-family: system-ui, -apple-system, BlinkMacSystemFont, "Inter", Roboto, sans-serif;
    font-size: 1rem;
    border-bottom: 1px solid #ccc;
    padding: 0.25rem;
  }
  @bottom-center {
    content: "${processedFooter.replace(/\[%MP%\]/g, totalPages.toString()).replace(/\[%CP%\]/g, 'counter(page)').replace(/"/g, '\\"')}";
    font-family: system-ui, -apple-system, BlinkMacSystemFont, "Inter", Roboto, sans-serif;
    font-size: 0.9rem;
    border-top: 1px solid #ccc;
    padding: 0.25rem;
  }
}
@page :first {
  @top-center { content: none; }
  @bottom-center { content: none; }
}
body { 
  background: white; 
  color: black; 
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Inter", Roboto, sans-serif;
  line-height: 1.6;
  margin: 0;
  padding: 0;
}
.document-container { 
  width: 21cm; 
  margin: 0 auto; 
  position: relative; 
  background: white; 
  min-height: 29.7cm; 
}
.cover-page {
  page-break-after: always;
  text-align: center;
  padding: 4cm 2cm;
  min-height: 29.7cm;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
.page-header { 
  position: fixed; 
  top: 0; 
  left: 0; 
  right: 0; 
  width: 100%; 
  height: 1.5cm; 
  text-align: center; 
  font-size: 1rem; 
  padding: 0.25rem; 
  border-bottom: 1px solid #ccc; 
  background: white; 
  z-index: 10; 
  display: flex; 
  align-items: center; 
  justify-content: center; 
  white-space: pre-wrap; 
}
.page-footer { 
  position: fixed; 
  bottom: 0; 
  left: 0; 
  right: 0; 
  width: 100%; 
  height: 1.5cm; 
  text-align: center; 
  font-size: 0.9rem; 
  padding: 0.25rem; 
  border-top: 1px solid #ccc; 
  background: white; 
  z-index: 10; 
  display: flex; 
  align-items: center; 
  justify-content: center; 
  white-space: pre-wrap; 
}
.watermark { 
  position: fixed; 
  top: 50%; 
  left: 50%; 
  transform: translate(-50%, -50%) rotate(-20deg); 
  font-size: 5rem; 
  color: #cccccc33; 
  z-index: 0; 
  pointer-events: none; 
  user-select: none; 
}
.content-area { 
  margin-top: 2cm; 
  margin-bottom: 2cm; 
  margin-left: 1cm; 
  margin-right: 1cm; 
  padding: 0; 
  box-sizing: border-box; 
  position: relative; 
  z-index: 10 !important; 
}
@media print { 
  .page-header, .page-footer { 
    display: none !important; 
  }
  .content-area { 
    margin-top: 2cm !important; 
    margin-bottom: 2cm !important; 
  }
  .watermark {
    display: none !important;
  }
}
</style>
</head>
<body>
<div class="document-container">
${coverPageHTML}
<div class="watermark">${watermarkText}</div>
<div class="content-area">${printableContent}</div>
</div>
<script>
window.onload = function() {
  console.log("Window loaded, preparing to print");
  
  setTimeout(function() {
    console.log("Attempting to print");
    window.print();
  }, 500);
};
</script>
</body>
</html>`;
    const printWindow = window.open('', `print_${Date.now()}`);
    if (!printWindow) return;
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  }

  return (
    <div className="p-3">
      <div className="flex justify-between mb-2">
        <div className="text-sm text-gray-500">
          Clean document view for printing or exporting as PDF.
        </div>
        <Button onClick={handlePrint} variant="outline" size="sm">
          <Printer className="mr-2 h-4 w-4" />
          Print / PDF
        </Button>
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Cover Page (optional)</label>
        <Textarea
          id="cover-page-input"
          className="w-full nodrag"
          placeholder="Enter Markdown for cover page (e.g., # Document Title)"
          rows={4}
          value={localCoverMarkdown}
          onChange={(e) => setLocalCoverMarkdown(e.target.value)}
          onBlur={handleCoverMarkdownBlur}
        />
        <div className="mt-2 text-xs text-gray-500">
          <p>Enter Markdown content for a cover page. This page will not have headers, footers, or watermarks.</p>
          <p className="mt-1 text-gray-400">Example: <code className="bg-gray-100 px-1 rounded"># [%DOCUMENT_TITLE%]\nAuthor: [%AUTHOR%]\nDate: [%DATE%]</code></p>
        </div>
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Header Text (optional)</label>
        <Textarea
          id="print-header-input"
          className="w-full nodrag"
          placeholder="Enter header text for printing"
          rows={2}
          value={localHeaderText}
          onChange={(e) => setLocalHeaderText(e.target.value)}
          onBlur={handleHeaderTextBlur}
        />
        <div className="mt-2 text-xs text-gray-500">
          <p className="font-medium mb-1">Available template variables:</p>
          <div className="grid grid-cols-2 gap-1">
            <span><code className="bg-gray-100 px-1 rounded">[%DATE%]</code> - Current date</span>
            <span><code className="bg-gray-100 px-1 rounded">[%TIME%]</code> - Current time</span>
            <span><code className="bg-gray-100 px-1 rounded">[%YEAR%]</code> - Current year</span>
            <span><code className="bg-gray-100 px-1 rounded">[%MONTH%]</code> - Current month name</span>
            <span><code className="bg-gray-100 px-1 rounded">[%DAY%]</code> - Current day</span>
            <span><code className="bg-gray-100 px-1 rounded">[%DOCUMENT_TITLE%]</code> - Document title</span>
            <span><code className="bg-gray-100 px-1 rounded">[%AUTHOR%]</code> - Document author</span>
            <span><code className="bg-gray-100 px-1 rounded">[%VERSION%]</code> - Version (1.0)</span>
            <span><code className="bg-gray-100 px-1 rounded">[%COMPANY%]</code> - Company name</span>
          </div>
          <p className="mt-2 text-gray-400">Example: <code className="bg-gray-100 px-1 rounded">[%DOCUMENT_TITLE%] - [%DATE%]</code></p>
        </div>
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Footer Text (optional)</label>
        <Textarea
          id="print-footer-input"
          className="w-full nodrag"
          placeholder="Enter footer text for printing"
          rows={2}
          value={localFooterText}
          onChange={(e) => setLocalFooterText(e.target.value)}
          onBlur={handleFooterTextBlur}
        />
        <div className="mt-2 text-xs text-gray-500">
          <p className="font-medium mb-1">Available template variables:</p>
          <div className="grid grid-cols-2 gap-1">
            <span><code className="bg-gray-100 px-1 rounded">[%CP%]</code> - Current page number</span>
            <span><code className="bg-gray-100 px-1 rounded">[%MP%]</code> - Total page count</span>
            <span><code className="bg-gray-100 px-1 rounded">[%DATE%]</code> - Current date</span>
            <span><code className="bg-gray-100 px-1 rounded">[%TIME%]</code> - Current time</span>
            <span><code className="bg-gray-100 px-1 rounded">[%YEAR%]</code> - Current year</span>
            <span><code className="bg-gray-100 px-1 rounded">[%DOCUMENT_TITLE%]</code> - Document title</span>
            <span><code className="bg-gray-100 px-1 rounded">[%AUTHOR%]</code> - Document author</span>
            <span><code className="bg-gray-100 px-1 rounded">[%VERSION%]</code> - Version (1.0)</span>
            <span><code className="bg-gray-100 px-1 rounded">[%COMPANY%]</code> - Company name</span>
          </div>
          <p className="mt-2 text-gray-400">Example: <code className="bg-gray-100 px-1 rounded">[%COMPANY%] - Page [%CP%] of [%MP%]</code></p>
        </div>
      </div>
      <div
        ref={printableRef}
        className={`${styles.markdownPreview} border rounded p-4 scrollable-content`}
        style={{
          height: isMarkdownExpanded ? markdownHeight : nodebaseHeight,
          maxHeight: isMarkdownExpanded ? 'none' : 'auto',
          overflow: 'auto',
          backgroundColor: 'white',
        }}
      >
        <div id="printable-content" className="font-sans text-2xl">
          <MarkdownRenderer
            markdown={cleanMarkdownForPrinting(contentMarkdown)}
          />
        </div>
      </div>
    </div>
  )
}

export default PrintingTab